import { createProductionResetState } from './production-reset';
import {
  getProductionPerPanelResult,
  getProductionPerStringResult
} from './production-mapping';

export function buildEmptyProductionState() {
  return {
    ...createProductionResetState(),
    productionHardware: null,
    productionIsRunning: false
  };
}

export function applyProductionReset(state) {
  return {
    ...state,
    ...buildEmptyProductionState()
  };
}

export function extractHourlyProduction(result) {
  const raw = result?.production_results;
  if (!raw || typeof raw !== 'object') return null;
  const payload = {};
  const keys = [
    'times',
    'pac_total_W',
    'pac_inverters_W',
    'pdc_inverters_W',
    'pdc_mppt_W',
    'clipping_W',
    'true_clip_W'
  ];
  keys.forEach((key) => {
    if (raw[key] !== undefined) payload[key] = raw[key];
  });
  return Object.keys(payload).length ? payload : null;
}

export function applySolarProductionResult(state, result = {}, ctx = {}) {
  const installationPackage = ctx.installationPackage || state.package || {};
  const selectedProductionPanel =
    ctx.selectedProductionPanel !== undefined
      ? ctx.selectedProductionPanel
      : installationPackage.panel || null;
  const currentInverterQuantity =
    ctx.currentInverterQuantity !== undefined
      ? ctx.currentInverterQuantity
      : installationPackage.inverterQuantity;
  const strings =
    ctx.strings !== undefined
      ? ctx.strings
      : result.solar_design?.[0]?.strings || null;
  const invertersUsed =
    ctx.invertersUsed !== undefined
      ? ctx.invertersUsed
      : result.solar_design?.[0]?.inverters_used || null;
  const hourlyProduction =
    ctx.hourlyProduction !== undefined
      ? ctx.hourlyProduction
      : extractHourlyProduction(result);
  const selectedPanelCount =
    ctx.selectedPanelCount !== undefined ? ctx.selectedPanelCount : 0;
  const productionPerPanel =
    ctx.productionPerPanel !== undefined
      ? ctx.productionPerPanel
      : selectedPanelCount > 0
        ? (result.annual_kwh || 0) / selectedPanelCount
        : 0;
  const systemSizeKw =
    ctx.systemSizeKw !== undefined
      ? ctx.systemSizeKw
      : result.system_size_kw !== undefined
        ? result.system_size_kw
        : null;
  const mpptCountTotal =
    ctx.mpptCountTotal !== undefined
      ? ctx.mpptCountTotal
      : result.mppt_count_total !== undefined
        ? result.mppt_count_total
        : null;
  const productionPerPanelData =
    ctx.productionPerPanelData !== undefined
      ? ctx.productionPerPanelData
      : getProductionPerPanelResult(result, null);
  const productionPerStringData =
    ctx.productionPerStringData !== undefined
      ? ctx.productionPerStringData
      : getProductionPerStringResult(result);
  const productionPanelSelection = ctx.productionPanelSelection || null;
  const updatedSegments =
    ctx.updatedSegments !== undefined ? ctx.updatedSegments : state.segments;

  const next = {
    ...state,
    segments: updatedSegments,
    productionHandled: true,
    totalAnnualKwh: result.annual_kwh || 0,
    totalMonthlyKwh: result.monthly_kwh || Array(12).fill(0),
    totalClippingKwh: result.annual_clipping_kwh || 0,
    totalMonthlyClippingKwh: result.monthly_clipping_kwh || Array(12).fill(0),
    dcAcRatio: result.dc_ac_ratio,
    stringingData: strings || null,
    invertersUsed,
    productionPerPanel,
    productionPerPanelData,
    productionPerStringData,
    hourlyProduction,
    productionHardware: {
      panel: selectedProductionPanel,
      inverter: installationPackage?.inverter || null,
      additionalInverters: installationPackage?.additional_inverters || [],
      inverterQuantity: currentInverterQuantity
    },
    selectedPanelCount,
    stringingPanelIndexMode: 'all',
    systemSizeKw,
    mpptCountTotal,
    productionIsRunning: false,
    productionSaveError: null,
    package: {
      ...installationPackage,
      panel: selectedProductionPanel || installationPackage?.panel,
      inverterQuantity: currentInverterQuantity
    }
  };

  if (productionPanelSelection?.hasSelectedPanels) {
    next.panels = productionPanelSelection.panels;
    next.selectedPanels = true;
    next.selectedObjects = {
      ...(state.selectedObjects || {}),
      panels: productionPanelSelection.selectedObjectsPanels
    };
  }

  if (ctx.stringingSelectedPanelIndexMap !== undefined) {
    next.stringingSelectedPanelIndexMap = ctx.stringingSelectedPanelIndexMap;
  }
  if (ctx.stringingRoofIndexMap !== undefined) {
    next.stringingRoofIndexMap = ctx.stringingRoofIndexMap;
  }

  return next;
}
